import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Users, CheckSquare, Home, MapPin, Navigation, Clock, TrendingUp, Calendar } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow, format, startOfDay, startOfMonth, subDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface UserStatisticsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

interface UserStats {
  customersCount: number;
  todosCount: number;
  unitsCount: number;
  lastActivity: string | null;
  areas: Array<{
    id: string;
    name: string;
    streetsCount: number;
    unitsCount: number;
    winsCount: number;
  }>;
  dailyActivity: Array<{
    date: string;
    count: number;
  }>;
  monthlyCount: number;
  todayCount: number;
}

export const UserStatisticsDialog = ({ isOpen, onClose, userId, userName }: UserStatisticsDialogProps) => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserStatistics();
    }
  }, [isOpen, userId]);

  const loadUserStatistics = async () => {
    setIsLoading(true);
    try {
      // Load customers count
      const { count: customersCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Load tasks count (open tasks)
      const { count: todosCount } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('completed', false);

      // Load units count and last activity
      const { data: unitsData, count: unitsCount } = await supabase
        .from('d2d_units')
        .select('last_contact_at', { count: 'exact' })
        .eq('user_id', userId)
        .order('last_contact_at', { ascending: false, nullsFirst: false })
        .limit(1);

      const lastActivity = unitsData?.[0]?.last_contact_at || null;

      // Load only own areas (not shared areas)
      const { data: areasData } = await supabase
        .from('d2d_areas')
        .select('id, name')
        .eq('created_by', userId);

      const allAreas = areasData || [];

      // For each area, count streets and units
      const areasWithStats = await Promise.all(
        allAreas.map(async (area: any) => {
          // Count streets
          const { count: streetsCount } = await supabase
            .from('d2d_streets')
            .select('*', { count: 'exact', head: true })
            .eq('area_id', area.id);

          // Count units via streets
          const { data: streets } = await supabase
            .from('d2d_streets')
            .select('id')
            .eq('area_id', area.id);

          const streetIds = streets?.map(s => s.id) || [];
          
          let areaUnitsCount = 0;
          let winsCount = 0;
          
          if (streetIds.length > 0) {
            const { data: houses } = await supabase
              .from('d2d_houses')
              .select('id')
              .in('street_id', streetIds);

            const houseIds = houses?.map(h => h.id) || [];
            
            if (houseIds.length > 0) {
              const { count } = await supabase
                .from('d2d_units')
                .select('*', { count: 'exact', head: true })
                .in('house_id', houseIds);
              
              areaUnitsCount = count || 0;

              // Count WIN tags
              const { count: winCount } = await supabase
                .from('d2d_units')
                .select('*', { count: 'exact', head: true })
                .in('house_id', houseIds)
                .contains('tags', ['WIN']);
              
              winsCount = winCount || 0;
            }
          }

          return {
            id: area.id,
            name: area.name,
            streetsCount: streetsCount || 0,
            unitsCount: areaUnitsCount,
            winsCount
          };
        })
      );

      // Load daily activity for the last 7 days
      const sevenDaysAgo = subDays(startOfDay(new Date()), 6);
      const { data: activityData } = await supabase
        .from('d2d_units')
        .select('last_contact_at')
        .eq('user_id', userId)
        .gte('last_contact_at', sevenDaysAgo.toISOString())
        .not('last_contact_at', 'is', null);

      // Group by date
      const dailyActivityMap = new Map<string, number>();
      for (let i = 0; i < 7; i++) {
        const date = subDays(startOfDay(new Date()), i);
        dailyActivityMap.set(format(date, 'yyyy-MM-dd'), 0);
      }

      activityData?.forEach(unit => {
        if (unit.last_contact_at) {
          const dateKey = format(startOfDay(new Date(unit.last_contact_at)), 'yyyy-MM-dd');
          if (dailyActivityMap.has(dateKey)) {
            dailyActivityMap.set(dateKey, (dailyActivityMap.get(dateKey) || 0) + 1);
          }
        }
      });

      const dailyActivity = Array.from(dailyActivityMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Today's count
      const todayCount = dailyActivityMap.get(format(startOfDay(new Date()), 'yyyy-MM-dd')) || 0;

      // Monthly count
      const monthStart = startOfMonth(new Date());
      const { count: monthlyCount } = await supabase
        .from('d2d_units')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('last_contact_at', monthStart.toISOString())
        .not('last_contact_at', 'is', null);

      setStats({
        customersCount: customersCount || 0,
        todosCount: todosCount || 0,
        unitsCount: unitsCount || 0,
        lastActivity,
        areas: areasWithStats,
        dailyActivity,
        monthlyCount: monthlyCount || 0,
        todayCount
      });
    } catch (error) {
      console.error('Error loading user statistics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Statistiken: {userName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : stats ? (
          <div className="space-y-4">
            {/* Last Activity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Letzte Aktivität
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.lastActivity ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {formatDistanceToNow(new Date(stats.lastActivity), {
                        addSuffix: true,
                        locale: de
                      })}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      ({new Date(stats.lastActivity).toLocaleString('de-DE')})
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Keine Aktivität erfasst</p>
                )}
              </CardContent>
            </Card>

            {/* Activity Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Aktivität
                </CardTitle>
                <CardDescription>
                  Bearbeitete Wohneinheiten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-primary">{stats.todayCount}</p>
                    <p className="text-xs text-muted-foreground">Heute</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      {stats.dailyActivity.reduce((sum, day) => sum + day.count, 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">7 Tage</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{stats.monthlyCount}</p>
                    <p className="text-xs text-muted-foreground">Monat</p>
                  </div>
                </div>
                
                <div className="h-[120px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.dailyActivity}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), 'dd.MM', { locale: de })}
                        className="text-xs"
                      />
                      <YAxis className="text-xs" />
                      <Tooltip 
                        labelFormatter={(date) => format(new Date(date), 'dd. MMMM yyyy', { locale: de })}
                        formatter={(value) => [value, 'Wohneinheiten']}
                      />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Kunden
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats.customersCount}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckSquare className="h-4 w-4" />
                    Offene Aufgaben
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats.todosCount}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Wohneinheiten
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats.unitsCount}</p>
                </CardContent>
              </Card>
            </div>

            {/* Areas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Gebiete ({stats.areas.length})
                </CardTitle>
                <CardDescription>
                  Übersicht aller Gebiete mit Straßen und Wohneinheiten
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.areas.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Keine Gebiete vorhanden
                  </p>
                ) : (
                  <div className="space-y-3">
                    {stats.areas.map((area) => (
                      <div
                        key={area.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="font-medium">{area.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Navigation className="h-3 w-3" />
                            <span>{area.streetsCount} Straßen</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            <span>{area.unitsCount} Einheiten</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <CheckSquare className="h-3 w-3 text-green-600" />
                            <span>{area.winsCount} Wins</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
